import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHelmet from '../../components/SEOHelmet';
import { BreadcrumbSchema, ItemListSchema } from '../../components/SchemaMarkup';
import { placeGuides } from '../../data/placeGuides';
import { destinations } from '../../data/destinations';
import { routes } from '../../data/routes';
import { FaArrowLeft, FaShare, FaRedo } from 'react-icons/fa';
import './Checklist.css';

// Items are flattened in a fixed order so a share link keeps its meaning.
const flatten = (packing) =>
  Object.entries(packing).flatMap(([group, items]) =>
    items.map((label) => ({ group, label }))
  );

// Checked state travels in the URL as a base36 bitmask, so a link carries the
// whole state with no account or server storage. Lists are well under 31 items,
// which keeps this inside a safe 32-bit integer.
const encode = (checked) => {
  let mask = 0;
  checked.forEach((isOn, i) => { if (isOn) mask |= 1 << i; });
  return mask === 0 ? '' : mask.toString(36);
};

const decode = (value, length) => {
  const out = new Array(length).fill(false);
  const mask = parseInt(value, 36);
  if (!value || Number.isNaN(mask)) return out;
  for (let i = 0; i < length; i += 1) out[i] = ((mask >> i) & 1) === 1;
  return out;
};

const Checklist = () => {
  const { country } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareMessage, setShareMessage] = useState('');

  const guide = placeGuides[country];
  const atlas = destinations.find((d) => d.id === country);
  const items = useMemo(() => (guide?.packing ? flatten(guide.packing) : []), [guide]);

  const [checked, setChecked] = useState(() => {
    const fromUrl = searchParams.get('p');
    if (fromUrl) return decode(fromUrl, items.length);
    try {
      const saved = JSON.parse(localStorage.getItem(`checklist:${country}`) || 'null');
      if (Array.isArray(saved) && saved.length === items.length) return saved;
    } catch { /* ignore unreadable storage */ }
    return new Array(items.length).fill(false);
  });

  useEffect(() => { window.scrollTo(0, 0); }, [country]);

  useEffect(() => {
    try {
      localStorage.setItem(`checklist:${country}`, JSON.stringify(checked));
    } catch { /* storage may be unavailable in private mode */ }
  }, [checked, country]);

  const toggle = useCallback((index) => {
    setChecked((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      const encoded = encode(next);
      setSearchParams(encoded ? { p: encoded } : {}, { replace: true });
      return next;
    });
  }, [setSearchParams]);

  const reset = () => {
    setChecked(new Array(items.length).fill(false));
    setSearchParams({}, { replace: true });
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${guide.name} packing list`, url });
        setShareMessage('Thanks for sharing');
      } else {
        await navigator.clipboard.writeText(url);
        setShareMessage('Link copied, progress included');
      }
    } catch (err) {
      if (err.name !== 'AbortError') setShareMessage('Unable to share');
    }
    setTimeout(() => setShareMessage(''), 3000);
  };

  if (!guide) {
    return (
      <div className="checklist-missing">
        <div className="container">
          <h1>No packing list for that destination</h1>
          <p>We only have lists for countries we have travelled ourselves.</p>
          <Link to="/places" className="btn btn-primary">Browse destinations</Link>
        </div>
      </div>
    );
  }

  const packedCount = checked.filter(Boolean).length;
  const progress = items.length ? Math.round((packedCount / items.length) * 100) : 0;
  const name = (guide.name || '').split(',')[0];

  return (
    <div className="checklist-page">
      <SEOHelmet
        title={`${name} Packing List — What to Pack, Free Checklist`}
        description={`A free ${name} packing list you can tick off and share. ${items.length} essentials covering clothing, accessories and electronics, based on what we actually carried.`}
        keywords={`${name} packing list, what to pack for ${name}, ${name} travel checklist`}
        url={`https://safarnamah.in/checklist/${country}`}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Destinations', url: 'https://safarnamah.in/places' },
        { name: name, url: `https://safarnamah.in/places/${country}` },
        { name: 'Packing list', url: `https://safarnamah.in/checklist/${country}` },
      ]} />
      <ItemListSchema name={`${name} packing list`} items={items.map((i) => i.label)} />

      <div className="container checklist-in">
        <Link to={`/places/${country}`} className="back-link">
          <FaArrowLeft aria-hidden="true" /> {name} guide
        </Link>

        <header className="checklist-head">
          <span className="kicker">Packing list</span>
          <h1>What to pack for {name}</h1>
          <p className="checklist-lead">
            {items.length} things worth carrying, drawn from the {name} guide.
            Tick as you pack. Your progress is saved on this device and travels in the
            link, so you can send it to whoever you are travelling with.
          </p>
        </header>

        <div className="checklist-bar" role="status" aria-live="polite">
          <div className="checklist-progress">
            <motion.span
              className="checklist-progress-fill"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <span className="checklist-count">{packedCount} of {items.length} packed</span>
          <div className="checklist-actions">
            <button type="button" onClick={share} className="checklist-btn">
              <FaShare aria-hidden="true" /> Share
            </button>
            <button type="button" onClick={reset} className="checklist-btn" disabled={!packedCount}>
              <FaRedo aria-hidden="true" /> Reset
            </button>
          </div>
          {shareMessage && <span className="checklist-msg">{shareMessage}</span>}
        </div>

        <div className="checklist-groups">
          {Object.entries(guide.packing).map(([group, groupItems]) => (
            <section className="checklist-group" key={group}>
              <h2>{group}</h2>
              <ul>
                {groupItems.map((label) => {
                  const index = items.findIndex((it) => it.group === group && it.label === label);
                  return (
                    <li key={label}>
                      <label className={checked[index] ? 'is-packed' : ''}>
                        <input
                          type="checkbox"
                          checked={!!checked[index]}
                          onChange={() => toggle(index)}
                        />
                        <span>{label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <aside className="checklist-foot">
          <h2>Before you go</h2>
          <p>
            {guide.visa?.summary}
            {atlas?.bestTime ? ` The best months to visit are ${atlas.bestTime}.` : ''}
          </p>
          <div className="checklist-foot-links">
            <Link to={`/places/${country}`} className="btn btn-secondary">Read the {name} guide</Link>
            {routes[country] && (
              <Link to={`/routes/${country}`} className="btn btn-secondary">See the {name} route</Link>
            )}
            <Link to="/plan-trip" className="btn btn-primary">Plan this trip with us</Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checklist;
